<script setup>
/**
 * The rail of one ticket type: the path a ticket walks.
 *
 * Both labels are shown on every row, side by side, because the pair is the
 * point — the internal name is what the team works with and the customer-facing
 * label is the only thing this engine ever tells a customer. A stage with no
 * customer label says so, rather than rendering an empty cell that reads as
 * missing data.
 *
 * Reordering emits the whole list of ids: the server takes the rail as a
 * permutation and refuses anything else, so a half-applied drag is not a state
 * this editor can produce. Removal is archival and is refused by the server when
 * it would leave the workflow with no way in or no way out — those refusals
 * arrive as messages and are shown as-is rather than second-guessed here.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';

import Button from 'dashboard/components-next/button/Button.vue';
import StagePill from 'dashboard/components-next/SupportCases/StagePill.vue';

const props = defineProps({
  // `{ id, name, customerLabel, category, position }`, in rail order.
  stages: {
    type: Array,
    default: () => [],
  },
  shareProgressWithCustomer: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['add', 'edit', 'remove', 'reorder']);

const { t } = useI18n();

const orderedStages = computed({
  get: () => props.stages,
  set: value =>
    emit(
      'reorder',
      value.map(stage => stage.id)
    ),
});

// Silence is the subtlest thing this engine does, so the rail says it out loud:
// each row states what the customer is told when a ticket enters it, in the words
// the message itself uses. A stage with no customer label says the customer hears
// nothing, which is a setting rather than an omission.
const customerPreview = stage =>
  stage.customerLabel
    ? t('SUPPORT_CASES.SETTINGS.STAGES.CUSTOMER_HEARS', {
        label: stage.customerLabel,
      })
    : t('SUPPORT_CASES.SETTINGS.STAGES.CUSTOMER_HEARS_NOTHING');

// Labels written while the toggle is off are kept, not lost — but nothing is
// sent, and an admin who has just written customer copy deserves to be told
// that before they wait for a message that never arrives.
const showSilentLabelsHint = computed(
  () =>
    !props.shareProgressWithCustomer &&
    props.stages.some(stage => Boolean(stage.customerLabel))
);
</script>

<template>
  <section class="flex flex-col gap-3" data-testid="stages-editor">
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-0.5">
        <h3 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.SETTINGS.STAGES.TITLE') }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('SUPPORT_CASES.SETTINGS.STAGES.DESCRIPTION') }}
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        color="slate"
        icon="i-lucide-plus"
        :label="t('SUPPORT_CASES.SETTINGS.STAGES.ADD')"
        :disabled="disabled"
        data-testid="stage-add"
        @click="emit('add')"
      />
    </div>

    <p
      v-if="showSilentLabelsHint"
      class="flex items-center gap-2 p-2 mb-0 text-xs rounded-lg text-n-slate-11 bg-n-alpha-1"
      data-testid="stages-silent-hint"
    >
      <span class="i-lucide-bell-off size-3.5 shrink-0" />
      {{ t('SUPPORT_CASES.SETTINGS.STAGES.CUSTOMER_DISABLED_HINT') }}
    </p>

    <Draggable
      v-model="orderedStages"
      item-key="id"
      handle="[data-drag-handle]"
      :disabled="disabled"
      class="flex flex-col gap-2"
      ghost-class="opacity-40"
    >
      <template #item="{ element: stage }">
        <div
          class="flex items-center gap-3 p-3 border rounded-lg border-n-weak bg-n-solid-1"
          :data-testid="`stage-row-${stage.id}`"
        >
          <span
            data-drag-handle
            class="text-n-slate-10 i-lucide-grip-vertical size-4 shrink-0"
            :class="disabled ? '' : 'cursor-grab'"
          />

          <div class="flex flex-col min-w-0 gap-1">
            <span class="text-sm truncate text-n-slate-12">
              {{ stage.name }}
            </span>
            <span
              class="text-xs truncate"
              :class="
                stage.customerLabel ? 'text-n-slate-11' : 'text-n-slate-10'
              "
              :data-testid="`stage-customer-label-${stage.id}`"
            >
              {{ customerPreview(stage) }}
            </span>
          </div>

          <div class="flex items-center gap-1 ltr:ml-auto rtl:mr-auto">
            <span
              v-if="stage.openCaseCount"
              class="text-xs tabular-nums text-n-slate-11"
              :data-testid="`stage-usage-${stage.id}`"
            >
              {{
                t('SUPPORT_CASES.SETTINGS.STAGES.OPEN_TICKETS', {
                  count: stage.openCaseCount,
                })
              }}
            </span>
            <StagePill :stage="stage" />
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-pencil"
              :disabled="disabled"
              :aria-label="t('SUPPORT_CASES.SETTINGS.STAGES.EDIT')"
              :data-testid="`stage-edit-${stage.id}`"
              @click="emit('edit', stage)"
            />
            <Button
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-trash-2"
              :disabled="disabled"
              :aria-label="t('SUPPORT_CASES.SETTINGS.STAGES.REMOVE')"
              :data-testid="`stage-remove-${stage.id}`"
              @click="emit('remove', stage)"
            />
          </div>
        </div>
      </template>
    </Draggable>
  </section>
</template>
